(function () {
  // Single, shared eBook add-on controller. Loaded once, site-wide, from
  // layout/theme.liquid. Any element with class "rw-ebook-checkbox" —
  // whether on the product page or in the cart drawer — is treated as one
  // instance of the same control.
  //
  // The user's "I want the eBook" choice is persisted (localStorage), not
  // just held in a short timing window. That's what lets the flow work:
  // check the box on a product page (cart may still be empty) -> click
  // Add to Cart -> the book lands in the cart a moment later -> the very
  // next tick sees a non-empty cart and attaches the eBook, however long
  // that gap was.
  if (window.__rwEbookAddonInit) return;
  window.__rwEbookAddonInit = true;

  const EBOOK_PROPERTY_KEY = '_ebook_access';
  const EBOOK_PROPERTY_VALUE = 'Digital Access';
  const EBOOK_PERCENT = 10;
  const POLL_INTERVAL = 2000;
  const DEBOUNCE_MS = 200;
  const INTENT_KEY = 'rw_ebook_wanted';

  const EBOOK_VARIANTS = {
    50: 48474789118197, 75: 48474789150965, 100: 47453825925365, 125: 48474789183733,
    150: 48474789216501, 175: 48474789249269, 200: 47453825958133, 225: 48474789282037,
    250: 48474789314805, 275: 48474789347573, 300: 47453825990901, 350: 48474789380341,
    400: 47453826023669, 450: 48474789413109, 500: 47453826056437, 550: 48474789445877,
    600: 47453826089205, 650: 48474789478645, 700: 48474789511413, 750: 48474789544181,
    800: 47453826121973, 900: 48474789576949, 1000: 47453826154741, 1100: 48474789609717,
    1200: 47453826187509, 1300: 48474789642485, 1400: 48474789675253, 1500: 47453826220277,
    1650: 48474789708021, 1800: 48474789740789, 1950: 48474789773557, 2000: 47453826253045,
    2100: 48474789806325, 2250: 48474789839093, 2300: 47453826285813, 2400: 48474789871861,
    2500: 47453826318581, 2550: 48474789904629, 2700: 48474789937397, 2850: 48474789970165,
    3000: 47453826351349, 3250: 48474790002933, 3500: 48089530040565, 4000: 48089530073333,
    4500: 48089530106101, 5000: 48089530138869, 5250: 48474790035701, 5500: 48474790068469,
    5750: 48474790101237, 6000: 48474790134005, 6500: 48479907676405, 7000: 48479907709173,
    7500: 48479907741941, 8000: 48479907774709, 8500: 48479907807477, 9000: 48479907840245,
    9500: 48479907873013, 10000: 48479907905781, 11000: 48479907938549, 12000: 48479907971317,
    13000: 48479908004085, 14000: 48479908036853, 15000: 48479908069621, 16000: 48479908102389,
    17000: 48479908135157, 18000: 48479908167925, 19000: 48479908200693, 20000: 48479908233461
  };

  let isProcessing = false;
  let debounceTimer = null;
  // Tracks whether we personally observed the eBook line item present in
  // the cart as of the last completed normalize() pass. Used to tell the
  // difference between "hasn't been added yet" (keep trying) and
  // "was there, now gone" (the customer removed it themselves via the
  // trash icon — respect that, don't resurrect it).
  let ebookWasPresent = false;

  function getIntent() {
    try {
      return localStorage.getItem(INTENT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setIntent(val) {
    try {
      localStorage.setItem(INTENT_KEY, val ? '1' : '0');
    } catch (e) { /* ignore */ }
  }

  function nearestTier(amount) {
    const tiers = Object.keys(EBOOK_VARIANTS).map(Number);
    return tiers.reduce((a, b) => Math.abs(b - amount) < Math.abs(a - amount) ? b : a);
  }

  async function getCart() {
    return fetch('/cart.js').then(r => r.json());
  }

  function findEbookItems(cart) {
    return cart.items.filter(i =>
      i.properties && i.properties[EBOOK_PROPERTY_KEY] === EBOOK_PROPERTY_VALUE
    );
  }

  async function removeEbookItems(items) {
    for (const item of items) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
    }
  }

  function getCheckboxes() {
    return Array.from(document.querySelectorAll('.rw-ebook-checkbox'));
  }

  function setAllCheckboxes(checked) {
    getCheckboxes().forEach(cb => { cb.checked = checked; });
  }

  function wireCheckboxes() {
    getCheckboxes().forEach(cb => {
      if (cb.dataset.rwWired) return;
      cb.dataset.rwWired = '1';
      // A freshly-appeared checkbox (new drawer render, product block,
      // etc.) starts in sync with the stored intent immediately.
      cb.checked = getIntent();
      cb.addEventListener('change', () => {
        setIntent(cb.checked);
        setAllCheckboxes(cb.checked);
        scheduleNormalize();
      });
    });
  }

  function scheduleNormalize() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(normalize, DEBOUNCE_MS);
  }

  // The cart drawer's item list is populated by the theme's own bundled
  // script as part of its normal page-load lifecycle. A partial DOM swap
  // (fetching the page and copying markup in) leaves it in a
  // pre-hydration, empty-looking state, since that script never re-runs
  // its population logic outside of a real navigation. A full reload is
  // the only reliable way to get a correctly-rendered drawer — the same
  // thing a manual refresh already does, just triggered automatically
  // right after a real cart change instead of requiring the customer to
  // do it themselves.
  //
  // Guarded with sessionStorage so it can only fire once per genuine
  // change: if a reload was just triggered and, on the very next tick,
  // something still looks "changed" (e.g. a transient race with another
  // script), we do NOT reload again — that's exactly what would cause a
  // reload loop and a permanently blank-looking drawer. The guard only
  // clears once a tick completes with nothing left to change, so a
  // genuinely new action (like unchecking the box later) can still
  // trigger its own single reload.
  const RELOAD_GUARD_KEY = 'rw_ebook_reload_guard';

  function reloadForFreshCart() {
    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') {
        return; // already reloaded once for this change — don't loop
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    } catch (e) { /* ignore */ }
    window.location.reload();
  }

  function clearReloadGuard() {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (e) { /* ignore */ }
  }

  async function normalize() {
    if (isProcessing) return;
    isProcessing = true;
    try {
      const wantEbook = getIntent();
      const cart = await getCart();
      const ebookItems = findEbookItems(cart);
      const presentNow = ebookItems.length > 0;

      if (!wantEbook) {
        if (presentNow) {
          await removeEbookItems(ebookItems);
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          reloadForFreshCart();
          return;
        }
        ebookWasPresent = false;
        clearReloadGuard();
        return;
      }

      // wantEbook is true from here on.
      if (!presentNow && ebookWasPresent) {
        // It existed a moment ago and is gone now — the customer removed
        // it themselves (e.g. the trash icon in the drawer/cart page).
        // Treat that as "I changed my mind", not as something to re-add.
        setIntent(false);
        setAllCheckboxes(false);
        ebookWasPresent = false;
        return;
      }

      if (!cart || cart.item_count === 0) {
        // Nothing to attach the eBook percentage to yet. Keep the intent
        // and checkbox as-is; a later tick (once the book actually lands
        // in the cart) will pick this back up.
        return;
      }
      const subtotal = cart.items.reduce((sum, item) => {
        if (item.properties && item.properties[EBOOK_PROPERTY_KEY] === EBOOK_PROPERTY_VALUE) return sum;
        return sum + item.line_price;
      }, 0) / 100;

      if (subtotal <= 0) return;

      const targetAmount = Math.round(subtotal * EBOOK_PERCENT / 100);
      const tier = nearestTier(targetAmount);
      const correctVariantId = EBOOK_VARIANTS[tier];

      let changed = false;
      for (const item of ebookItems) {
        if (item.id !== correctVariantId || ebookItems.length > 1) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
          changed = true;
        }
      }

      const cleanCart = await getCart();
      const hasCorrect = cleanCart.items.some(i =>
        i.id === correctVariantId &&
        i.properties && i.properties[EBOOK_PROPERTY_KEY] === EBOOK_PROPERTY_VALUE
      );

      if (!hasCorrect) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: correctVariantId,
              quantity: 1,
              properties: {
                [EBOOK_PROPERTY_KEY]: EBOOK_PROPERTY_VALUE,
                "_ebook_calc": EBOOK_PERCENT + "% of cart value"
              }
            }]
          })
        });
        changed = true;
      }

      ebookWasPresent = true;

      if (changed) {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        reloadForFreshCart();
        return;
      }

      // Nothing needed changing this pass — state is stable, so clear
      // the guard to allow a future genuine change to reload again.
      clearReloadGuard();
    } finally {
      isProcessing = false;
    }
  }

  async function tick() {
    wireCheckboxes();
    setAllCheckboxes(getIntent());
    await normalize();
  }

  document.addEventListener('DOMContentLoaded', tick);

  document.addEventListener('cart:updated', () => {
    wireCheckboxes();
    scheduleNormalize();
  });

  const observer = new MutationObserver(() => wireCheckboxes());
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  setInterval(tick, POLL_INTERVAL);
})();
